/** @jsxImportSource @emotion/react */
'use memo'

// web/src/features/event/components/EventDetailActions.tsx
import css from '@emotion/css/macro'
import { Tooltip } from 'antd'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { paragraphXSmall, weightMedium } from 'src/components/css'
import ShareIcon from 'src/components/icons/ShareIcon'

interface EventDetailActionsProps {
  canEdit: boolean
  canDelete: boolean
  onEdit: () => void
  onDelete: () => void
  /** Opens the share modal. Available to ANY viewer — see the guard below. */
  onShare: () => void
}

/**
 * Edit / delete controls for the event detail header.
 *
 * Styling mirrors `ProfilePlayerActions` (the player profile header) so the
 * two read as one system: a filled PRIMARY button for the main action, and a
 * 32px square outlined button for the icon-only one. Replaces the bare pencil
 * and trash glyphs that previously sat inline beside the title.
 */
const EventDetailActions: React.FC<EventDetailActionsProps> = ({
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onShare,
}) => {
  // No early return on the permissions any more: sharing needs none, so a
  // read-only viewer still gets a toolbar holding just the share button.

  return (
    <div css={containerStyle}>
      {canEdit ? (
        <button type='button' css={editButtonStyle} onClick={onEdit}>
          <EditOutlined />
          {capitalizeWords('Edit Event')}
        </button>
      ) : null}

      <Tooltip title={capitalizeWords('Share Event')}>
        <button
          type='button'
          css={iconButtonStyle}
          onClick={onShare}
          aria-label='Share event'
        >
          {/* The shared SVG the Game page's share trigger uses, NOT antd's
              ShareAltOutlined — the two draw different glyphs and the arrow is
              the established mark. Colour is passed explicitly: this SVG takes
              a `color` prop and defaults to SOLITUDE, so unlike the antd icon
              beside it, it does not inherit the button's `color`. */}
          <ShareIcon size={14} color={colors.NEUTRAL_0} />
        </button>
      </Tooltip>

      {canDelete ? (
        <Tooltip title={capitalizeWords('Delete Event')}>
          <button
            type='button'
            css={iconButtonStyle}
            onClick={onDelete}
            aria-label='Delete event'
          >
            <DeleteOutlined />
          </button>
        </Tooltip>
      ) : null}
    </div>
  )
}

export default EventDetailActions

// ---- styled ----
/* Mirrors ProfilePlayerActions' button family. Kept local rather than
   imported: those styles are private module constants over in profile, and
   lifting them into a shared module is a wider refactor than this warrants. */
const containerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`

const baseButtonStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  ${paragraphXSmall}
  ${weightMedium}
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.85;
  }

  .anticon {
    font-size: 16px;
  }
`

/* Content colour comes from PRIMARY_BUTTON_CONTENT — colors.PRIMARY is tenant
   switched (SportNinja amber / Canlan blue), so a hardcoded light or dark
   value would be unreadable on one of them. */
const editButtonStyle = css`
  ${baseButtonStyle}
  background-color: ${colors.PRIMARY};
  color: ${PRIMARY_BUTTON_CONTENT.text};
  border: none;
`

const iconButtonStyle = css`
  ${baseButtonStyle}
  background-color: transparent;
  color: ${colors.NEUTRAL_0};
  border: 1px solid ${colors.PRIMARY};
  width: 32px;
  height: 32px;
  padding: 0;
`
